import { useMemo, useState } from 'react';

import { IconClose } from '../../../common/icons';
import { formatDateOnly } from '../../../common/utils/date';
import { useOpuList } from '../../opu/contexts/opu';
import ClientFarmSearch from '../../clients/forms/search';
import { useCrosses } from '../contexts/asocebu';
import CrossRow from '../forms/cross-row';

import './styles/worklist.css';

// Tab "Tickets de cruces" (admin-asocebu.md). El backend devuelve SOLO cruces
// reportables (transferencias con preñez confirmed|born), con `opu` por cruce y
// `farm`/`pregnancy` por transfer. El front AGRUPA: OPU → sesión T.E. (finca) →
// cruces. Filtros server-side: has_ticket, cliente/finca, OPU, y rango por fecha TE.
const HAS_TICKET = [
    { value: 'false', label: 'Por asignar' },
    { value: 'true',  label: 'Con ticket' },
    { value: '',      label: 'Todos' },
];

export default function CrossTicketsWorklist() {
    const [hasTicket, setHasTicket] = useState('false');
    const [opu, setOpu] = useState('');
    const [scope, setScope] = useState({ client_id: '', farm_id: '' }); // cliente XOR finca
    const [active, setActive] = useState(null);                          // chip del buscador compuesto
    const [from, setFrom] = useState('');
    const [to, setTo] = useState('');

    const { data: opuData } = useOpuList({ limit: 500 });
    const opus = useMemo(() => opuData?.items ?? [], [opuData]);

    const filters = useMemo(() => {
        const out = { limit: 500 };
        if (hasTicket) out.has_ticket = hasTicket;
        if (opu) out.opu = opu;
        if (scope.client_id) out.client_id = scope.client_id;
        if (scope.farm_id) out.farm_id = scope.farm_id;
        if (from) out.from = from;
        if (to) out.to = to;
        return out;
    }, [hasTicket, opu, scope, from, to]);

    const { data, isLoading, isFetching, error } = useCrosses(filters);
    const items = useMemo(() => data?.items ?? [], [data]);
    const total = data?.pagination?.total ?? items.length;
    const truncated = total > items.length;

    // Árbol OPU → T.E.(finca) → cruces. Un cruce (clivage) vive en una OPU pero sus
    // transferencias pueden caer en distintas T.E. → aparece bajo cada T.E. con sus
    // receptoras de esa sesión (el ticket es del cruce, igual en todas).
    const tree = useMemo(() => {
        const opuMap = new Map();
        for (const item of items) {
            const oKey = item.opu?.id ?? 'none';
            if (!opuMap.has(oKey)) opuMap.set(oKey, { opu: item.opu, teMap: new Map() });
            const oNode = opuMap.get(oKey);
            for (const t of (item.transfers || [])) {
                const tKey = t.te_session?.id ?? 'none';
                if (!oNode.teMap.has(tKey)) oNode.teMap.set(tKey, { te: t.te_session, farm: t.farm, crossMap: new Map() });
                const tNode = oNode.teMap.get(tKey);
                if (!tNode.crossMap.has(item.clivage_id)) tNode.crossMap.set(item.clivage_id, { item, transfers: [] });
                tNode.crossMap.get(item.clivage_id).transfers.push(t);
            }
        }
        const byDateDesc = (a, b) => String(b.te?.date || '').localeCompare(String(a.te?.date || ''));
        return [...opuMap.values()]
            .sort((a, b) => (b.opu?.id ?? 0) - (a.opu?.id ?? 0))
            .map((o) => ({
                opu: o.opu,
                tes: [...o.teMap.values()].sort(byDateDesc).map((te) => ({
                    te: te.te, farm: te.farm, crosses: [...te.crossMap.values()],
                })),
            }));
    }, [items]);

    const pick = (it, type) => {
        if (type === 'farm') {
            setScope({ client_id: '', farm_id: String(it.id) });
            setActive({ type, label: `${it.name}${it.owner?.name ? ` · ${it.owner.name}` : ''}` });
        } else {
            setScope({ client_id: String(it.id), farm_id: '' });
            setActive({ type, label: it.name });
        }
    };
    const clearScope = () => { setScope({ client_id: '', farm_id: '' }); setActive(null); };

    return (
        <div className="aso">
            <header className="aso__header">
                <div className="aso__heading">
                    <h1 className="aso__title">Tickets de cruces</h1>
                    <span className="aso__count">{total}</span>
                </div>
                <p className="aso__subtitle">Cruces preñados (tras dx), agrupados por OPU y sesión de T.E., para asociar el ticket de ASOCEBU.</p>
            </header>

            <div className="aso__toolbar">
                <div className="aso__seg" role="group" aria-label="Estado del ticket">
                    {HAS_TICKET.map((o) => (
                        <button
                            key={o.value || 'all'}
                            type="button"
                            className={`aso__seg-btn ${hasTicket === o.value ? 'aso__seg-btn--active' : ''}`}
                            onClick={() => setHasTicket(o.value)}
                        >
                            {o.label}
                        </button>
                    ))}
                </div>

                <div className="aso__filter aso__filter--search">
                    <span className="aso__filter-label">Cliente / finca</span>
                    {active ? (
                        <div className="aso-active">
                            <span className="aso-active__label">{active.type === 'farm' ? 'Finca' : 'Cliente'}</span>
                            <span className="aso-active__value">{active.label}</span>
                            <button type="button" className="aso-active__clear" onClick={clearScope} aria-label="Quitar filtro"><IconClose /></button>
                        </div>
                    ) : (
                        <ClientFarmSearch scope="all" placeholder="Cliente o finca…" onSelect={pick} />
                    )}
                </div>

                <label className="aso__filter">
                    <span className="aso__filter-label">Sesión OPU</span>
                    <select value={opu} onChange={(e) => setOpu(e.target.value)} aria-label="Filtrar por sesión OPU">
                        <option value="">Todas las OPU</option>
                        {opus.map((o) => <option key={o.id} value={o.id}>{o.code} · #{o.id}</option>)}
                    </select>
                </label>

                <label className="aso__filter">
                    <span className="aso__filter-label">Desde (fecha TE)</span>
                    <input type="date" value={from} max={to || undefined} onChange={(e) => setFrom(e.target.value)} />
                </label>
                <label className="aso__filter">
                    <span className="aso__filter-label">Hasta</span>
                    <input type="date" value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} />
                </label>
            </div>

            {error && <div className="aso__error">{error.message || 'No se pudo cargar el worklist'}</div>}
            {truncated && <p className="aso__note">Mostrando los primeros {items.length} de {total} cruces. Acotá por OPU, cliente/finca o fecha de la TE.</p>}

            {isLoading ? (
                <div className="aso__state">Cargando cruces…</div>
            ) : !items.length ? (
                <div className="aso__state">{hasTicket === 'false' ? 'No hay cruces preñados por asignar.' : 'No hay cruces para el filtro.'}</div>
            ) : (
                <div className={`aso__list ${isFetching ? 'aso__list--busy' : ''}`}>
                    {tree.map((o) => (
                        <section key={o.opu?.id ?? 'none'} className="aso-opu">
                            <header className="aso-opu__head">
                                <h2 className="aso-opu__title">{o.opu?.code || 'OPU —'}</h2>
                                {o.opu?.id != null && <span className="aso-opu__id">#{o.opu.id}</span>}
                            </header>
                            {o.tes.map((te) => (
                                <div key={te.te?.id ?? 'none'} className="aso-te">
                                    <header className="aso-te__head">
                                        <span className="aso-te__farm">{te.farm?.name || 'Sin finca'}</span>
                                        <span className="aso-te__code">{te.te?.code || '—'}</span>
                                        {te.te?.date && <span className="aso-te__date">{formatDateOnly(te.te.date)}</span>}
                                    </header>
                                    <div className="aso-te__crosses">
                                        {te.crosses.map((c) => (
                                            <CrossRow key={c.item.clivage_id} item={c.item} transfers={c.transfers} />
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </section>
                    ))}
                </div>
            )}
        </div>
    );
}
